// 戴冠獣「灰の咆哮」(houndRoar・床の帯 kind 'shock') の見た目 — めくれる床 (2026-10-20)。
//
// 作者の選択 = 床技7つの見本の B「めくれる床」(docs/research/2026-10-19-floor-fx-mihon.md §5・台帳 production.md 10-19 夜)。
//   「声の圧で床石が手前から順にめくれ、前へ転がり跳ねる」。作者の物差し「名前通り作りたい」
//   = 王の咆哮の圧が床を這い、石畳を剥がして前へ吹き飛ばす。灰が舞う。
// 段:
//   ① 予告 (1450ms): 帯の床石の継ぎ目から冷たい照りが漏れ、石が細かく震えて浮く。灰が床を這って前へ流れる。
//      最後の 25% は獣の側の石から順に縁が持ち上がりかける (脈打つ)。
//   ② 当たり 0〜110ms: 獣の側から前へ順に、石の板が手前の縁からめくれ上がる (約5コマ)。下から灰が噴く。
//   ③ 当たり 〜450ms: めくれた板が前へ転がって跳ね、小石が散る。
//   ④ 当たりの終わり〜後: 板が床に落ちて薄れ、灰が沈んで晴れる。
// 絵の部品 = Codex が1枚に描いた物を切り出した atlas (元絵 そざい/concept/hound-roar-floor-1020/・
//   切り出しの記録は LUNAIRE-notes/assets/2026-10-20-hound-roar-floor/)。動きはコード。
// 描くだけ。当たり (場所・時間・ダメージ) は変えない。絵の基準点は全部 帯の ±BLEED の中。
// 色: 照りは今の境界線と同じ冷たい青白 (合図の色を変えない)。赤・金・白い線は使わない。
// 吠えの圧 (src/hound_howl_fx.ts) はのしかかりの溜めの中だけ。この技とは重ならない。
// 前の配布版 (儀式の境界線・render.ts の shock) は `?houndRoarFx=line`。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'

export type HoundRoarFx = 'floor' | 'line'
let roarFx: HoundRoarFx = 'floor'

/** game.ts の URL ブロックだけが呼ぶ。指定なし (null)・知らない値は採用済みの既定 (めくれる床)。 */
export function setHoundRoarFx(v: string | null): void { roarFx = v === 'line' ? 'line' : 'floor' }
export function getHoundRoarFx(): HoundRoarFx { return roarFx }

// ---------------------------------------------------------------- 部品の絵

type Rect = readonly [number, number, number, number]
// 名前: slab_i (床石の板・水平の向き)・chunk_i (小石)・ashLow_i (床を這う灰)・ashUp_i (巻き上がる灰)。
export const ROAR_ATLAS: Record<string, Rect> = {
  slab_0: [0, 0, 81, 58],
  slab_1: [83, 0, 86, 63],
  slab_2: [171, 0, 83, 61],
  slab_3: [256, 0, 87, 61],
  slab_4: [345, 0, 89, 63],
  slab_5: [0, 65, 82, 61],
  slab_6: [84, 65, 77, 63],
  slab_7: [163, 65, 97, 58],
  chunk_0: [262, 65, 40, 43],
  chunk_1: [304, 65, 22, 24],
  chunk_2: [328, 65, 27, 28],
  chunk_3: [357, 65, 27, 34],
  chunk_4: [386, 65, 28, 32],
  chunk_5: [416, 65, 25, 25],
  chunk_6: [443, 65, 25, 29],
  chunk_7: [470, 65, 17, 20],
  chunk_8: [489, 65, 14, 19],
  chunk_9: [0, 130, 18, 21],
  chunk_10: [20, 130, 17, 20],
  chunk_11: [39, 130, 7, 7],
  chunk_12: [48, 130, 9, 10],
  chunk_13: [59, 130, 12, 11],
  chunk_14: [73, 130, 9, 9],
  chunk_15: [84, 130, 11, 10],
  ashLow_0: [97, 130, 135, 50],
  ashLow_1: [234, 130, 130, 56],
  ashLow_2: [366, 130, 120, 42],
  ashUp_0: [0, 188, 168, 100],
  ashUp_1: [170, 188, 131, 105],
}
const ROAR_SRC = '/sprites/fx/hazard/hound_roar_floor_atlas.png'
let roarImg: CanvasImageSource | null = null
let roarLoading = false
/** 部品の絵の読み込み (1回)。 */
export function preloadHoundRoarFloorAtlas(): void {
  if (roarImg || roarLoading || typeof Image === 'undefined') return
  roarLoading = true
  const img = new Image()
  img.onload = () => { roarImg = img }
  img.onerror = () => { roarLoading = false }
  img.src = ROAR_SRC
}
/** テスト用 (偽の絵を差す)。 */
export function setHoundRoarFloorAtlasForTest(img: CanvasImageSource | null): void { roarImg = img }

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
function count(prefix: string): number {
  let n = 0
  while (ROAR_ATLAS[`${prefix}_${n}`]) n += 1
  return n
}

// 部品1つを、(ax, ay) を基準に回して描く。基準は下端中央 (pivotY=1) か中心 (pivotY=0.5)。dw×dh はゲームの px。
function part(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, name: string,
  ax: number, ay: number, dw: number, dh: number, rot: number, a: number, flip = false, pivotY = 1,
): void {
  if (a <= 0.01 || dw <= 0.5 || dh <= 0.5) return
  const r = ROAR_ATLAS[name]
  if (!r) return
  ctx.save()
  ctx.globalAlpha = Math.min(1, a)
  ctx.translate(ax, ay)
  if (rot) ctx.rotate(rot)
  if (flip) ctx.scale(-1, 1)
  ctx.drawImage(img, r[0], r[1], r[2], r[3], -dw / 2, -dh * pivotY, dw, dh)
  ctx.restore()
}

// ---------------------------------------------------------------- 帯の配置

const BLEED = 8              // 帯の左右へはみ出してよい幅 (部品の基準点)
const SWEEP_MS = 110         // 獣の側から帯の先まで、めくれが走る時間
const FLIP_MS = 85           // 板1枚がめくれ上がる時間
const AIR1_MS = 250          // 1回目の跳び
const AIR2_MS = 150          // 2回目 (小さく跳ねる)
const LINGER_MS = 520        // 当たりの後、板が薄れて灰が晴れるまで
const FLOOR_CUT = 5          // 床に埋まった石を切る線 (floorY から下へ)

type Slab = { name: string; x0: number; len: number; th: number; delay: number; jump: number; travel: number; spin: number; flip: boolean; seed: number }
type Geom = { xStart: number; xEnd: number; w: number; cx: number; dir: number; near: number; seed: number; slabs: Slab[] }

function geomOf(hz: ActiveHit): Geom {
  const w = hz.xEnd - hz.xStart
  const cx = (hz.xStart + hz.xEnd) / 2
  // 獣の向き。帯は獣の前 (originX + dir*170) にあるので、帯の中心と originX の位置から決める。
  const dir = hz.originX !== undefined && hz.originX !== cx ? Math.sign(cx - hz.originX) : (hz.originDir ?? 1)
  const near = dir > 0 ? hz.xStart : hz.xEnd
  const seed = hz.id * 2.37 + 41.3
  const nSlab = Math.max(1, count('slab'))
  // 板の数は帯の幅から (260px で6枚)。長さは少しずつ変える。
  const n = Math.max(2, Math.min(8, Math.round(w / 50)))
  const slabs: Slab[] = []
  for (let i = 0; i < n; i += 1) {
    const s = seed + i * 11.3
    const d = ((i + 0.5) / n) * w            // 獣の側の縁からの距離
    const x0 = near + dir * d
    const name = `slab_${(i + Math.floor(hash01(seed) * nSlab)) % nSlab}`
    const r = ROAR_ATLAS[name]
    const len = (w / n) * (0.92 + hash01(s) * 0.22)
    const th = r ? len * (r[3] / r[2]) : 30
    // 先の板ほど遠くへ転がらない (帯の先 ±BLEED を越えない)。
    const room = Math.max(0, w - d + BLEED - len * 0.3)
    const travel = Math.min(room, 26 + hash01(s + 1) * 34)
    slabs.push({
      name, x0, len, th, delay: (d / w) * SWEEP_MS + hash01(s + 2) * 14,
      jump: 30 + hash01(s + 3) * 30 + (i === 0 ? 10 : 0), travel,
      spin: (1.4 + hash01(s + 4) * 1.8) * (hash01(s + 5) < 0.25 ? -0.5 : 1),
      flip: hash01(s + 6) < 0.5, seed: s,
    })
  }
  return { xStart: hz.xStart, xEnd: hz.xEnd, w, cx, dir, near, seed, slabs }
}

function clampX(g: Geom, x: number): number { return Math.max(g.xStart - BLEED, Math.min(g.xEnd + BLEED, x)) }

// ---------------------------------------------------------------- 描く

// 床の継ぎ目から漏れる冷たい照り (今の境界線と同じ青白)。帯の中だけ・横長の楕円で四角い縁を作らない。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.beginPath()
  ctx.rect(g.xStart - BLEED, floorY - h, g.w + BLEED * 2, h + 4)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2 + BLEED) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(160,190,240,${(0.34 * a).toFixed(3)})`)
  gr.addColorStop(0.55, `rgba(140,160,230,${(0.12 * a).toFixed(3)})`)
  gr.addColorStop(1, 'rgba(140,160,230,0)')
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h + 4)
  ctx.restore()
}

// めくれた跡 (板があった所) の照り。板ごとに一瞬強く光って引く。
function seamGlow(ctx: CanvasRenderingContext2D, g: Geom, sl: Slab, floorY: number, a: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  const x = clampX(g, sl.x0)
  const hw = sl.len * 0.5
  ctx.translate(x, floorY)
  ctx.scale(hw / 10, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, 10)
  gr.addColorStop(0, `rgba(190,205,250,${(0.55 * a).toFixed(3)})`)
  gr.addColorStop(1, 'rgba(150,165,235,0)')
  ctx.fillStyle = gr
  ctx.fillRect(-10, -10, 20, 12)
  ctx.restore()
}

// 床を這う灰 (低い)。k=何個目か、q=0..1 の流れ、a=濃さ。
function drawAshLow(ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, floorY: number, q: number, a: number, k: number): void {
  const nAsh = count('ashLow')
  if (!nAsh || a <= 0.01) return
  const s = g.seed + k * 5.7
  const name = `ashLow_${(k + Math.floor(hash01(s) * nAsh)) % nAsh}`
  const r = ROAR_ATLAS[name]!
  const dw = Math.min(g.w * 0.55, 90 + hash01(s + 1) * 50) * (0.85 + 0.25 * q)
  const dh = Math.min(dw * (r[3] / r[2]), 40)
  const span = g.w * 0.75
  const off = (g.w * (0.12 + 0.2 * k) + span * 0.35 * q) % (g.w + 1)
  const x = clampX(g, g.near + g.dir * off)
  part(ctx, img, name, x, floorY + 6, dw, dh, 0, a, g.dir < 0)
}

/** 予告 (telegraph)。絵が無ければ false (呼び手は前の境界線で描く)。 */
export function drawHoundRoarTelegraph(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number): boolean {
  const img = roarImg
  if (!img) { preloadHoundRoarFloorAtlas(); return false }
  const g = geomOf(hz)
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const late = p >= 0.75
  const pulse = late ? 1 + 0.2 * Math.sin(timeSec * 16) : 1
  underGlow(ctx, g, floorY, (0.3 + 0.6 * p) * pulse, 26 + 22 * p)
  // 灰が床を這って前へ流れる (帯の中)。
  for (let k = 0; k < 3; k += 1) drawAshLow(ctx, img, g, floorY, (p * 1.6 + k * 0.33) % 1, (0.22 + 0.4 * p) * smooth01(0, 0.25, p), k)
  // 床石: 床の下からせり上がり、震える。終盤は獣の側から順に縁が持ち上がりかける。
  //   床の線で切る (床に埋まった石が浮いてくる＝床の上に置いたれんがに見せない)。
  const rise = smooth01(0, 0.55, p)
  floorClip(ctx, g, floorY)
  for (const sl of g.slabs) {
    const d01 = Math.abs(sl.x0 - g.near) / Math.max(1, g.w)
    const shake = Math.sin(timeSec * (38 + hash01(sl.seed) * 14) + sl.seed * 5) * (0.6 + 1.6 * p)
    const pry = smooth01(0.72 + d01 * 0.18, 1, p) * (0.12 + 0.06 * Math.sin(timeSec * 20 + sl.seed))
    const lift = 1 + 2.5 * p + shake
    drawSlabAt(ctx, img, g, sl, floorY, sl.x0, -lift * rise, pry, 0.55 + 0.45 * smooth01(0, 0.6, p), sl.th * (1.05 - 0.5 * rise))
  }
  ctx.restore()
  return true
}

// 床の線より下を描かない (ctx.save 済みで呼び、呼び手が restore する)。
function floorClip(ctx: CanvasRenderingContext2D, g: Geom, floorY: number): void {
  ctx.save()
  ctx.beginPath()
  ctx.rect(g.xStart - BLEED - 80, floorY - 400, g.w + BLEED * 2 + 160, 400 + FLOOR_CUT)
  ctx.clip()
}

// 板1枚。ang=めくれの角度 (0=床に寝た向き)。獣から遠い側の縁を支点に、手前の縁から持ち上がる。
//   いちばん低い所が floorY+dy+embed に来るように置く (embed>0 で床へ半分沈む＝床石に見せる)。
function drawSlabAt(
  ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, sl: Slab, floorY: number,
  x: number, dy: number, ang: number, a: number, embed: number,
): void {
  const hl = sl.len / 2
  const ht = sl.th / 2
  const xc = x + g.dir * hl * (1 - Math.cos(Math.min(Math.abs(ang), Math.PI / 2)))
  const yc = floorY + dy + embed - (Math.abs(Math.sin(ang)) * hl + Math.abs(Math.cos(ang)) * ht)
  part(ctx, img, sl.name, clampX(g, xc), yc, sl.len, sl.th, g.dir * ang, a, sl.flip, 0.5)
}

/** 当たり (active)。絵が無ければ false。 */
export function drawHoundRoarActive(ctx: CanvasRenderingContext2D, hz: ActiveHit, timeSec: number, floorY: number): boolean {
  const img = roarImg
  if (!img) { preloadHoundRoarFloorAtlas(); return false }
  const ms = hz.elapsedMs - hz.telegraphMs
  remember(hz, timeSec - ms / 1000)
  drawRoarAt(ctx, img, geomOf(hz), floorY, ms, hz.activeMs)
  return true
}

function drawRoarAt(ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, floorY: number, ms: number, activeMs: number): void {
  const end = activeMs
  const fade = 1 - smooth01(end - 40, end + LINGER_MS, ms)
  underGlow(ctx, g, floorY, (ms < 140 ? 1.3 - ms / 500 : 1) * fade * 0.8, 40)
  // 床を這う灰は当たりの間に濃くなって晴れる。
  for (let k = 0; k < 3; k += 1) drawAshLow(ctx, img, g, floorY, clamp01(0.4 + ms / (end + LINGER_MS)), 0.6 * fade, k)
  for (const sl of g.slabs) {
    const t = ms - sl.delay
    if (t < 0) {
      floorClip(ctx, g, floorY)
      drawSlabAt(ctx, img, g, sl, floorY, sl.x0, -3, 0.14, 1, sl.th * 0.55)
      ctx.restore()
      continue
    }
    seamGlow(ctx, g, sl, floorY, (1 - smooth01(0, 320, t)) * 1.2)
    drawAshUp(ctx, img, g, sl, floorY, t)
    const slabA = 1 - smooth01(end - 60 - sl.delay * 0.5, end + LINGER_MS * 0.75, ms)
    if (t < FLIP_MS) {
      // めくれ上がる: 手前の縁から 0 → 約 1.25rad。
      const q = easeOut(t / FLIP_MS)
      floorClip(ctx, g, floorY)
      drawSlabAt(ctx, img, g, sl, floorY, sl.x0, -3 - 10 * q, 0.14 + 1.1 * q, slabA, sl.th * 0.55 * (1 - q))
      ctx.restore()
    } else {
      // 前へ転がって跳ねる (2回)。回りは跳びの間だけ。
      const ta = t - FLIP_MS
      const q1 = clamp01(ta / AIR1_MS)
      const q2 = clamp01((ta - AIR1_MS) / AIR2_MS)
      const fx = easeOut(clamp01(ta / (AIR1_MS + AIR2_MS)))
      const x = sl.x0 + g.dir * sl.travel * fx
      const hop = ta < AIR1_MS ? 4 * q1 * (1 - q1) * sl.jump + 13 * (1 - q1) : 4 * q2 * (1 - q2) * sl.jump * 0.32
      const air = clamp01(ta / (AIR1_MS + AIR2_MS))
      const rot = 1.24 + sl.spin * easeOut(air) * Math.PI * 0.5
      // 落ちた後は板が床に寝る (回りを最寄りの水平へ寄せる)。
      const rest = smooth01(0.85, 1, air)
      const flat = Math.round(rot / Math.PI) * Math.PI
      drawSlabAt(ctx, img, g, sl, floorY, x, -hop, rot + (flat - rot) * rest, slabA, 0)
    }
    drawChunks(ctx, img, g, sl, floorY, t)
  }
}

// めくれた板の下から噴く灰 (斜め前へ巻き上がる)。t=その板のめくれ始めからの ms。
function drawAshUp(ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, sl: Slab, floorY: number, t: number): void {
  const nUp = count('ashUp')
  if (!nUp || t > 760) return
  const q = clamp01(t / 760)
  const name = `ashUp_${Math.floor(hash01(sl.seed + 20) * nUp)}`
  const r = ROAR_ATLAS[name]!
  const dh = (46 + hash01(sl.seed + 21) * 30) * (0.45 + 0.75 * easeOut(q * 1.6))
  const dw = dh * (r[2] / r[3])
  const x = clampX(g, sl.x0 + g.dir * 18 * easeOut(q))
  const a = 0.7 * smooth01(0, 0.06, q) * (1 - smooth01(0.35, 1, q))
  part(ctx, img, name, x, floorY + 4, dw, dh, g.dir * 0.12, a, g.dir < 0)
}

// 板から散る小石。前へ・上へ跳び、落ちる。
function drawChunks(ctx: CanvasRenderingContext2D, img: CanvasImageSource, g: Geom, sl: Slab, floorY: number, t: number): void {
  const nCh = count('chunk')
  if (!nCh || t > 620) return
  for (let k = 0; k < 4; k += 1) {
    const s = sl.seed + 30 + k * 3.9
    const born = 20 + hash01(s) * 60
    const life = 340 + hash01(s + 1) * 200
    const q = (t - born) / life
    if (q <= 0 || q >= 1) continue
    const name = `chunk_${Math.floor(hash01(s + 2) * nCh)}`
    const r = ROAR_ATLAS[name]!
    const size = 5 + hash01(s + 3) * 8
    const vx = g.dir * (30 + hash01(s + 4) * 70) * (hash01(s + 5) < 0.2 ? -0.5 : 1)
    const vy = 50 + hash01(s + 6) * 70
    const tt = q * life / 1000
    const x = clampX(g, sl.x0 + vx * tt)
    const y = Math.min(floorY, floorY - 10 - vy * tt + 380 * tt * tt)
    const a = 1 - smooth01(0.7, 1, q)
    part(ctx, img, name, x, y, size, size * (r[3] / r[2]), (hash01(s + 7) - 0.5) * 8 * q, a, false, 0.5)
  }
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(hz: ActiveHit, startSec: number): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { g: geomOf(hz), startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (板が床で薄れ、灰が晴れる)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawHoundRoarLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  const img = roarImg
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + LINGER_MS) { traces.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    if (img) drawRoarAt(ctx, img, tr.g, floorY, ms, tr.activeMs)
  }
}

if (roarFx === 'floor' && typeof window !== 'undefined') preloadHoundRoarFloorAtlas()
